import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '../lib/api';

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('GBP');
  const [symbol, setSymbol] = useState('£');
  const [rate, setRate] = useState(1);
  const [currencies, setCurrencies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Load saved preference
    const saved = localStorage.getItem('preferred_currency');
    
    // Fetch currencies and detect location
    Promise.all([
      fetch(`${API_URL}/api/currencies`).then(r => r.json()).catch(() => null),
      !saved ? fetch(`${API_URL}/api/currencies/detect`).then(r => r.json()).catch(() => null) : null
    ]).then(([currData, detectData]) => {
      if (currData?.currencies) {
        setCurrencies(currData.currencies);
        
        // Use saved preference, detected, or default GBP
        const targetCode = saved || detectData?.currency || 'GBP';
        const found = currData.currencies.find(c => c.code === targetCode);
        if (found) {
          setCurrency(found.code);
          setSymbol(found.symbol);
          setRate(found.rate);
        }
      }
      setLoaded(true);
    });
  }, []);

  const changeCurrency = (code) => {
    const found = currencies.find(c => c.code === code);
    if (found) {
      setCurrency(found.code);
      setSymbol(found.symbol);
      setRate(found.rate);
      localStorage.setItem('preferred_currency', code);
    }
  };

  // Convert GBP amount to selected currency
  const convert = (gbpAmount) => {
    if (!gbpAmount || gbpAmount === 0) return 0;
    return gbpAmount * rate;
  };

  // Format price in selected currency
  const formatPrice = (gbpAmount) => {
    if (!gbpAmount || gbpAmount === 0) return 'Free';
    const converted = convert(gbpAmount);
    // For currencies with large values (NGN, KES, etc), no decimals
    const noDecimals = ['NGN', 'GHS', 'KES', 'JPY', 'KRW', 'UGX', 'TZS', 'RWF', 'XOF', 'XAF', 'COP', 'INR'];
    const decimals = noDecimals.includes(currency) ? 0 : 2;
    return `${symbol}${converted.toFixed(decimals)}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, symbol, rate, currencies, convert, formatPrice, changeCurrency, loaded }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) return { currency: 'GBP', symbol: '£', rate: 1, formatPrice: (a) => `£${(a||0).toFixed(2)}`, changeCurrency: () => {}, currencies: [], loaded: false };
  return context;
};
